import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraSwimlaneTimeline } from '@mono/ui-components/captains';
import { SpecraBadge } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraRestrictedChip, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { registryHref } from '../../registryHref';
import { formatVehicleTime, fractionalYear, monthYear } from './vehicleUi';
const title = 'Task orders we track on it · who held each round';
export function roundSegments(round) {
    if (!round.start || !round.end) return [];
    const ours = round.holderKind === 'US';
    const tone = ours ? 'ours' : round.status === 'PRIOR' || round.holderKind === 'PRIOR' ? 'legacy' : 'theirs';
    return [
        {
            id: round.roundKey,
            start: fractionalYear(round.start),
            end: fractionalYear(round.end),
            label: ours ? 'Us' : round.holder?.name || 'Holder unknown',
            tone,
            href: round.contract ? registryHref(round.contract) ?? undefined : undefined
        },
        ...round.optionsEnd && fractionalYear(round.optionsEnd) > fractionalYear(round.end) ? [
            {
                id: `${round.roundKey}-options`,
                start: fractionalYear(round.end),
                end: fractionalYear(round.optionsEnd),
                label: ours ? `PoP to ${monthYear(round.optionsEnd)}` : 'options',
                tone: ours ? 'ours-future' : 'theirs-future'
            }
        ] : []
    ];
}
export function TaskOrderRounds({ data, dossier: d, year }) {
    if (data.growthHidden || data.requirements.length === 0) return /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: title,
                action: data.growthHidden && data.growthPresence ? /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "teal",
                            children: "Delivery"
                        }),
                        /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                            label: "Growth is working this"
                        })
                    ]
                }) : undefined
            }),
            !(data.growthHidden && data.growthPresence) && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                size: "compact",
                title: data.growthHidden ? 'Task-order history is Growth landscape data.' : 'No task orders tracked on this vehicle yet.'
            })
        ]
    });
    const orderingEnd = data.orderingEnd ?? d.orderingEnd;
    const hatchVisible = !!orderingEnd && fractionalYear(orderingEnd) < year + 3;
    const successor = d.successors.find((edge)=>edge.relation === 'SUCCESSOR' && !edge.archivedAt)?.successor;
    const lanes = data.requirements.map((requirement)=>({
            id: requirement.seatKey,
            label: requirement.name,
            sublabel: [
                requirement.customer?.name,
                requirement.valueLabel
            ].filter(Boolean).join(' · '),
            segments: [
                ...requirement.rounds.flatMap(roundSegments),
                ...hatchVisible && orderingEnd ? [
                    {
                        id: `${requirement.seatKey}-closed`,
                        start: Math.max(year - 7, fractionalYear(orderingEnd)),
                        end: year + 3,
                        label: '',
                        title: 'Ordering closed',
                        tone: 'other',
                        pattern: 'hatched'
                    }
                ] : []
            ],
            markers: requirement.nextRoundExpectedAt && requirement.nextRoundVehicle ? [
                {
                    id: `${requirement.seatKey}-next`,
                    at: fractionalYear(requirement.nextRoundExpectedAt),
                    kind: 'next-round',
                    title: `next round → ${requirement.nextRoundVehicle.name}`,
                    label: `next round → ${requirement.nextRoundVehicle.name}`
                }
            ] : []
        }));
    return /*#__PURE__*/ _jsx(SpecraSwimlaneTimeline, {
        "data-testid": Testids.registryVehicle.roundsChart,
        title: title,
        density: "compact",
        formatTime: formatVehicleTime,
        renderLink: renderRouterLink,
        scale: {
            start: year - 7,
            end: year + 3,
            now: data.now ? fractionalYear(data.now) : undefined
        },
        nowLabel: data.now ? `Now · ${monthYear(data.now)}` : undefined,
        periods: Array.from({
            length: 10
        }, (_, i)=>({
                id: String(year - 7 + i),
                label: String(year - 7 + i),
                start: year - 7 + i,
                end: year - 6 + i
            })),
        bands: [
            {
                id: 'rounds',
                kind: 'lanes',
                lanes
            }
        ],
        legend: [
            {
                label: 'Us',
                tone: 'ours'
            },
            {
                label: 'Competitor',
                tone: 'theirs'
            },
            {
                label: 'Earlier holder',
                tone: 'legacy'
            },
            {
                label: 'Options / remaining PoP',
                tone: 'theirs-future'
            },
            {
                label: 'Our remaining PoP',
                tone: 'ours-future'
            },
            ...hatchVisible && orderingEnd ? [
                {
                    label: 'Ordering closed',
                    tone: 'other',
                    shape: 'outline'
                }
            ] : []
        ],
        insight: orderingEnd ? `Ordering on ${d.name} ends ${monthYear(orderingEnd)}${hatchVisible ? ' (hatched)' : ''}.${successor ? ` Every seat that comes up after that moves to ${successor.name}, so who holds ${successor.name} decides who can bid.` : ''}` : undefined,
        table: {
            columns: [
                {
                    key: 'seat',
                    label: 'Task order'
                },
                {
                    key: 'customer',
                    label: 'Customer'
                },
                {
                    key: 'value',
                    label: 'Award value'
                },
                {
                    key: 'holder',
                    label: 'Holder / period'
                },
                {
                    key: 'start',
                    label: 'From'
                },
                {
                    key: 'end',
                    label: 'To'
                }
            ],
            rows: data.requirements.flatMap((r)=>{
                const lane = lanes.find((l)=>l.id === r.seatKey);
                return (lane?.segments ?? []).map((segment)=>({
                        id: `${r.seatKey}:${segment.id}`,
                        cells: {
                            seat: r.name,
                            customer: r.customer?.name,
                            value: r.valueLabel,
                            holder: segment.title ?? segment.label,
                            start: formatVehicleTime(segment.start),
                            end: formatVehicleTime(segment.end)
                        }
                    }));
            })
        },
        emptyMessage: "No task orders tracked on this vehicle yet."
    });
}
